import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine, ScatterChart, Scatter, BarChart, Bar } from "recharts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function EvaluationTab({ id }) {
  const [ev, setEv] = useState(null);
  useEffect(() => {
    const load = () => endpoints.evaluation(id).then((r) => setEv(r.data.evaluation));
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [id]);

  if (!ev) return <div className="text-sm text-muted-foreground">Evaluation not ready…</div>;

  return (
    <div className="space-y-8">
      {ev.problem_type === "classification" ? <ClassificationEval ev={ev} /> : <RegressionEval ev={ev} />}
      {ev.feature_importance && (
        <div>
          <div className="label-mono mb-3">// FEATURE IMPORTANCE</div>
          <div className="border border-border rounded-sm p-4 bg-card h-96">
            <ResponsiveContainer>
              <BarChart data={ev.feature_importance.slice().reverse()} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" stroke="hsl(0 0% 45%)" fontSize={10} />
                <YAxis dataKey="feature" type="category" stroke="hsl(0 0% 45%)" fontSize={10} width={180} />
                <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 14%)" }} />
                <Bar dataKey="importance" fill="hsl(45 93% 47%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function ClassificationEval({ ev }) {
  return (
    <Tabs defaultValue="cm">
      <TabsList>
        <TabsTrigger value="cm" data-testid="tab-cm">Confusion Matrix</TabsTrigger>
        {ev.roc && <TabsTrigger value="roc" data-testid="tab-roc">ROC Curve</TabsTrigger>}
        {ev.pr && <TabsTrigger value="pr" data-testid="tab-pr">PR Curve</TabsTrigger>}
      </TabsList>
      <TabsContent value="cm">
        {ev.confusion_matrix && <ConfusionMatrix cm={ev.confusion_matrix} />}
      </TabsContent>
      {ev.roc && (
        <TabsContent value="roc">
          <div className="border border-border rounded-sm p-4 bg-card h-96">
            <ResponsiveContainer>
              <LineChart data={ev.roc.fpr.map((f, i) => ({ fpr: f, tpr: ev.roc.tpr[i] }))}>
                <XAxis dataKey="fpr" stroke="hsl(0 0% 45%)" fontSize={10} label={{ value: "FPR", fill: "hsl(0 0% 45%)", fontSize: 10, position: "insideBottom", offset: -5 }} />
                <YAxis stroke="hsl(0 0% 45%)" fontSize={10} label={{ value: "TPR", fill: "hsl(0 0% 45%)", fontSize: 10, angle: -90, position: "insideLeft" }} />
                <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 14%)" }} />
                <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]} stroke="hsl(0 0% 30%)" strokeDasharray="3 3" />
                <Line type="monotone" dataKey="tpr" stroke="hsl(45 93% 47%)" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>
      )}
      {ev.pr && (
        <TabsContent value="pr">
          <div className="border border-border rounded-sm p-4 bg-card h-96">
            <ResponsiveContainer>
              <LineChart data={ev.pr.recall.map((r, i) => ({ recall: r, precision: ev.pr.precision[i] }))}>
                <XAxis dataKey="recall" stroke="hsl(0 0% 45%)" fontSize={10} label={{ value: "Recall", fill: "hsl(0 0% 45%)", fontSize: 10, position: "insideBottom", offset: -5 }} />
                <YAxis stroke="hsl(0 0% 45%)" fontSize={10} label={{ value: "Precision", fill: "hsl(0 0% 45%)", fontSize: 10, angle: -90, position: "insideLeft" }} />
                <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 14%)" }} />
                <Line type="monotone" dataKey="precision" stroke="hsl(142 71% 45%)" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>
      )}
    </Tabs>
  );
}

function ConfusionMatrix({ cm }) {
  const max = Math.max(...cm.matrix.flat());
  return (
    <div className="border border-border rounded-sm p-6 bg-card">
      <div className="inline-block">
        <div className="flex ml-24">
          {cm.labels.map((l) => (
            <div key={l} style={{ width: 80 }} className="text-[10px] font-mono text-center text-muted-foreground pb-2">
              pred: {l}
            </div>
          ))}
        </div>
        {cm.matrix.map((row, i) => (
          <div key={i} className="flex items-center">
            <div className="w-24 pr-3 text-[10px] font-mono text-right text-muted-foreground">actual: {cm.labels[i]}</div>
            {row.map((v, j) => {
              const intensity = max > 0 ? v / max : 0;
              const bg = `hsl(45 93% ${20 + intensity * 30}%)`;
              return (
                <div key={j} style={{ width: 80, height: 80, background: bg }} className="grid place-items-center border border-black/40">
                  <span className="num font-bold text-lg text-white">{v}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function RegressionEval({ ev }) {
  const data = (ev.actual || []).map((a, i) => ({ actual: a, predicted: ev.predicted[i], residual: ev.residuals[i] }));
  return (
    <Tabs defaultValue="scatter">
      <TabsList>
        <TabsTrigger value="scatter" data-testid="tab-scatter">Predicted vs Actual</TabsTrigger>
        <TabsTrigger value="residuals" data-testid="tab-residuals">Residuals</TabsTrigger>
      </TabsList>
      <TabsContent value="scatter">
        <div className="border border-border rounded-sm p-4 bg-card h-96">
          <ResponsiveContainer>
            <ScatterChart>
              <XAxis dataKey="actual" stroke="hsl(0 0% 45%)" fontSize={10} label={{ value: "Actual", fill: "hsl(0 0% 45%)", fontSize: 10, position: "insideBottom", offset: -5 }} />
              <YAxis dataKey="predicted" stroke="hsl(0 0% 45%)" fontSize={10} label={{ value: "Predicted", fill: "hsl(0 0% 45%)", fontSize: 10, angle: -90, position: "insideLeft" }} />
              <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 14%)" }} />
              <Scatter data={data} fill="hsl(45 93% 47%)" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </TabsContent>
      <TabsContent value="residuals">
        <div className="border border-border rounded-sm p-4 bg-card h-96">
          <ResponsiveContainer>
            <ScatterChart>
              <XAxis dataKey="predicted" stroke="hsl(0 0% 45%)" fontSize={10} />
              <YAxis dataKey="residual" stroke="hsl(0 0% 45%)" fontSize={10} />
              <ReferenceLine y={0} stroke="hsl(0 0% 40%)" strokeDasharray="3 3" />
              <Tooltip contentStyle={{ background: "hsl(0 0% 6%)", border: "1px solid hsl(0 0% 14%)" }} />
              <Scatter data={data} fill="hsl(217 91% 60%)" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </TabsContent>
    </Tabs>
  );
}
